import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import {
  AudienceSet,
  AudienceSetTargetsData,
  AudienceSetTargetType,
  ChangeEventType,
} from '../../../types';
import Spinner from '../../Spinner';
import { DEFAULT_COLOR_THEME, ScrollStyle } from '../../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  constantStringToHumanized,
  humanizeString,
} from '../../../utils/stringModifier';
import AudienceList from '../Assetz/Facebook/Audience/AudienceList';
import { isEmptyTargets } from '../../../utils/helpers/AssetHelpers';
import { numberShortener } from '../../../utils/numberFormatter';
import { isArray } from 'lodash';
import { PrimaryButton } from '../../Buttons';
import AddIcon from '@mui/icons-material/Add';

interface DiyAudienceSetFormProps {
  audienceSets: AudienceSet[];
  filteredAudienceSets: AudienceSet[];
  setFilteredAudienceSets: Dispatch<SetStateAction<AudienceSet[]>>;
  selectedAudienceSet: AudienceSet;
  setSelectedAudienceSet: Dispatch<SetStateAction<AudienceSet>>;
  reachError: string;
  reachLoading: boolean;
  reachEstimate: any;
  setOpenAudienceSetForm: Dispatch<SetStateAction<boolean>>;
  setAudienceSetAction: Dispatch<SetStateAction<'create' | 'edit'>>;
  onSelectAudienceSet: (audienceSet: AudienceSet) => void;
  loading: boolean;
}

const DiyAudienceSetForm: FC<DiyAudienceSetFormProps> = ({
  audienceSets,
  filteredAudienceSets,
  setFilteredAudienceSets,
  selectedAudienceSet,
  setSelectedAudienceSet,
  reachError,
  reachEstimate,
  reachLoading,
  setOpenAudienceSetForm,
  setAudienceSetAction,
  onSelectAudienceSet,
  loading,
}) => {
  const [search, setSearch] = useState<string>('');

  const handleOnSearch = (e: ChangeEventType) => {
    setSearch(e.target.value);
    const temp = audienceSets.filter((audienceSet: AudienceSet) =>
      audienceSet.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );

    setFilteredAudienceSets(temp);
  };

  const handleOnClickCreate = () => {
    setOpenAudienceSetForm(true);
    setSelectedAudienceSet(null);
    setAudienceSetAction('create');
  };

  const handleOnClickEdit = (audienceSet: AudienceSet) => {
    setOpenAudienceSetForm(true);
    setSelectedAudienceSet(audienceSet);
    setAudienceSetAction('edit');
  };

  const renderIncludedTargets = () => {
    if (
      selectedAudienceSet?.targets?.custom_audiences?.length === 0 &&
      selectedAudienceSet?.targets?.flexible_spec?.length === 0
    ) {
      return (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            No Included Targets
          </Typography>
        </Grid>
      );
    }

    if (selectedAudienceSet?.audienceType === 'detailed') {
      return selectedAudienceSet?.targets?.flexible_spec?.map(
        (type: AudienceSetTargetType, index: number) => {
          if (isEmptyTargets(type)) return <Box key={`detailed-${index}`} />;

          return (
            <Grid item xs={12} key={`detailed-${index}`}>
              <Grid container spacing={2}>
                {index > 0 ? (
                  <Grid item xs={12}>
                    <Divider>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        and must also match
                      </Typography>
                    </Divider>
                  </Grid>
                ) : null}

                {Object?.keys(type || {})?.map((key: string, index: number) => {
                  if (type?.[key]?.length === 0 || !isArray(type?.[key])) {
                    return <Box key={`detailed-${key}-${index}`} />;
                  }

                  return (
                    <Grid item xs={12} key={`detailed-${key}-${index}`}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {constantStringToHumanized(key)}
                          </Typography>
                        </Grid>

                        {type?.[key]?.map((target: AudienceSetTargetsData) => {
                          return (
                            <Grid item xs={12} key={`detailed-${target.id}`}>
                              <AudienceList target={target} />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          );
        },
      );
    }

    return selectedAudienceSet?.targets?.custom_audiences?.map(
      (customAudience: AudienceSetTargetsData) => {
        return (
          <Grid item xs={12} key={customAudience.id}>
            <AudienceList target={customAudience} />
          </Grid>
        );
      },
    );
  };

  const renderExcludedTargets = () => {
    if (
      isEmptyTargets(selectedAudienceSet?.targets?.exclusions) &&
      selectedAudienceSet?.targets?.excluded_custom_audiences?.length === 0
    ) {
      return (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            No Excluded Targets
          </Typography>
        </Grid>
      );
    }

    if (selectedAudienceSet?.audienceType === 'detailed') {
      return Object?.keys(selectedAudienceSet?.targets?.exclusions)?.map(
        (key: string, index: number) => {
          if (selectedAudienceSet?.targets?.exclusions[key]?.length === 0) {
            return <Box key={`exclusions-${key}-${index}`} />;
          }

          return (
            <Grid item xs={12} key={`exclusions-${key}-${index}`}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {constantStringToHumanized(key)}
                  </Typography>
                </Grid>

                {selectedAudienceSet?.targets?.exclusions?.[key]?.map(
                  (target: AudienceSetTargetsData) => {
                    return (
                      <Grid item xs={12} key={`exclusions-${target.id}`}>
                        <AudienceList target={target} />
                      </Grid>
                    );
                  },
                )}
              </Grid>
            </Grid>
          );
        },
      );
    }

    return selectedAudienceSet?.targets?.excluded_custom_audiences?.map(
      (customAudience: AudienceSetTargetsData) => {
        return (
          <Grid item xs={12} key={customAudience.id}>
            <AudienceList target={customAudience} />
          </Grid>
        );
      },
    );
  };

  const buildDescription = (audienceSet: AudienceSet) => {
    let text: string[] = [];
    if (audienceSet.audienceType === 'ai') {
      text = [...text, 'AI'];
    } else {
      text = [...text, humanizeString(audienceSet.audienceType)];
    }

    if (audienceSet.description) {
      text = [...text, audienceSet.description];
    }

    if (audienceSet.minReach && audienceSet.maxReach) {
      text = [
        ...text,
        `Est. Audience: ${numberShortener(
          audienceSet.minReach,
        )} - ${numberShortener(audienceSet.maxReach)}`,
      ];
    }

    return text.join('\n');
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sm={4}
        sx={{
          minHeight: `calc(100vh - 250px)`,
          maxHeight: `calc(100vh - 250px)`,
          overflowY: 'auto',
          overflowX: 'hidden',
          ...ScrollStyle(),
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 999,
            backgroundColor: 'white',
            mb: 2,
          }}
        >
          <Grid item xs={12}>
            <PrimaryButton
              title="Add Audience Set"
              type="button"
              handleOnClick={handleOnClickCreate}
              startIcon={<AddIcon />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              id="search"
              name="search"
              label="Search"
              onChange={handleOnSearch}
              value={search}
              InputLabelProps={{ shrink: true }}
              size="small"
              placeholder="Search by audience set name"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          {!loading ? (
            filteredAudienceSets?.length > 0 ? (
              filteredAudienceSets.map((audienceSet: AudienceSet) => {
                const selected = selectedAudienceSet?._id === audienceSet._id;

                return (
                  <Grid
                    item
                    xs={12}
                    key={audienceSet._id}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    {selected ? (
                      <Box
                        component="div"
                        sx={{
                          position: 'absolute',
                          top: '8px',
                          zIndex: 1,
                          right: '-10px',
                          float: 'right',
                          margin: '1px',
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: DEFAULT_COLOR_THEME,
                            bgcolor: 'InfoBackground',
                            borderRadius: '50%',
                          }}
                        />
                      </Box>
                    ) : null}

                    <Card
                      sx={{
                        height: '100%',
                        border: selected ? 'solid' : '',
                        borderColor: selected ? DEFAULT_COLOR_THEME : '',
                        borderWidth: 'medium',
                        zIndex: 0,
                      }}
                    >
                      <CardActionArea
                        onClick={() => onSelectAudienceSet(audienceSet)}
                      >
                        <CardHeader
                          title={audienceSet.name}
                          subheader={buildDescription(audienceSet)}
                          titleTypographyProps={{
                            variant: 'body1',
                            sx: { fontWeight: 'bold' },
                          }}
                          subheaderTypographyProps={{
                            variant: 'body2',
                            sx: { whiteSpace: 'pre-wrap' },
                          }}
                        />
                      </CardActionArea>

                      <CardActions
                        sx={{ justifyContent: 'flex-end', display: 'flex' }}
                      >
                        <PrimaryButton
                          title="Edit"
                          type="button"
                          variant="text"
                          handleOnClick={() => handleOnClickEdit(audienceSet)}
                        />
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                mt={2}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', textAlign: 'center' }}
                >
                  No data to show
                </Typography>

                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  Click Add Audience Set to create a new one
                </Typography>
              </Grid>
            )
          ) : (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
              mt={2}
            >
              <Spinner />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        sm={8}
        sx={{
          minHeight: `calc(100vh - 250px)`,
          maxHeight: `calc(100vh - 250px)`,
          overflowY: 'auto',
          ...ScrollStyle(),
        }}
      >
        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          <Grid item xs={12} sx={{ flexDirection: 'column', display: 'flex' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Estimated Audience Size:{' '}
              <Typography
                component="span"
                variant="body1"
                sx={{ marginLeft: '10px' }}
              >
                {reachLoading ? (
                  <Spinner />
                ) : (
                  `${numberShortener(
                    reachEstimate.users_lower_bound,
                  )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                )}
              </Typography>
            </Typography>

            {reachError ? (
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', color: 'red' }}
              >
                {reachError}
              </Typography>
            ) : null}
          </Grid>

          {selectedAudienceSet?.audienceType === 'custom' ? (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight="bold">
                  Advantage Lookalike:{' '}
                  <Typography variant="body2" component="span">
                    {selectedAudienceSet?.targets?.targeting_relaxation_types
                      ?.lookalike === 1
                      ? 'Enabled'
                      : 'Disabled'}
                  </Typography>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" fontWeight="bold">
                  Advantage+ Audience:{' '}
                  <Typography variant="body2" component="span">
                    {selectedAudienceSet?.targets?.targeting_automation
                      ?.advantage_audience === 1
                      ? 'Enabled'
                      : 'Disabled'}
                  </Typography>
                </Typography>
              </Grid>
            </>
          ) : null}

          {selectedAudienceSet?.audienceType === 'detailed' ? (
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                Advantage Detailed Targeting:{' '}
                <Typography variant="body2" component="span">
                  {selectedAudienceSet?.targets?.targeting_optimization ===
                  'expansion_all'
                    ? 'Enabled'
                    : 'Disabled'}
                </Typography>
              </Typography>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                marginRight: '10px',
                textAlign: 'center',
              }}
            >
              Included
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                marginRight: '10px',
                textAlign: 'center',
              }}
            >
              Included
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  {renderIncludedTargets()}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                marginRight: '10px',
                textAlign: 'center',
              }}
            >
              Excluded
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  {renderExcludedTargets()}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiyAudienceSetForm;
